/* GrowthOS — Account detail "Contacts" tab.
   UI ported from the claude.ai/design "growthos" AccountRecord.Contacts (a list of
   contacts, avatar + name + title/email, reusing the Opportunities row layout).
   The design's rows borrow lead-only fields (stage badge, deal value) because its
   mock dataset has no separate Contact entity — real Contacts don't have a stage or
   deal value, so those are omitted here. Wired to the real per-account contact list
   (apiServices.getContactByModule) instead of the design's `D.leads` mock filter.
   "Add Contact" navigates to the real add-contact form (blank — the form has no
   account-prefill support to wire into, same as the design's button, which has no
   handler either). `contacts`/`loading` are fetched once by the parent (viewAccount)
   so the tab-bar count badge and this card stay in sync off a single API call. */
import { useNavigate } from 'react-router-dom';
import { Ic } from "../../shared/component/GosIcon";


const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const avaColor = (name) => AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length];

const AccountContactsTab = ({ contacts = [], loading = false, canAdd = true }) => {
  const navigate = useNavigate();

  return (
    <div className="gos-oppr-card">
      <div className="gos-oppr-head">
        <div className="gos-oppr-title">
          <Ic name="contacts" size={16} />
          Contacts
          <span className="gos-oppr-count">{contacts.length}</span>
        </div>
        {canAdd ? (
          <button type="button" className="gos-oppr-btn soft" onClick={() => navigate('/contacts/addContact')}>
            <Ic name="plus" size={13} />
            Add Contact
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="gos-oppr-empty">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </div>
      ) : contacts.length === 0 ? (
        <div className="gos-oppr-empty">
          <div className="gos-oppr-empty-ic">
            <Ic name="contacts" size={21} />
          </div>
          <div className="gos-oppr-empty-t">No contacts</div>
          <div className="gos-oppr-empty-s">Contacts linked to this account will appear here.</div>
        </div>
      ) : (
        <div className="gos-oppr-list">
          {contacts.map((c, i) => {
            const name = c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || '(no name)';
            return (
              <div
                key={c.id ?? i}
                className={'gos-oppr-row' + (i === contacts.length - 1 ? ' last' : '')}
                style={{ cursor: c.id ? 'pointer' : 'default' }}
                onClick={() => c.id && navigate(`/contacts/viewContact?id=${c.id}`, { state: c })}
              >
                <span className="gos-bulk-ava" style={{ background: avaColor(name) }}>{initials(name)}</span>
                <div className="gos-oppr-main">
                  <div className="gos-oppr-name">{name}</div>
                  <div className="gos-oppr-meta">
                    {c.title ? (
                      <>
                        <Ic name="briefcase" size={12} />
                        <span>{c.title}</span>
                      </>
                    ) : null}
                    {c.email ? (
                      <>
                        {c.title ? <span className="gos-oppr-dot">·</span> : null}
                        <Ic name="mail" size={12} />
                        <span>{c.email}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <Ic name="chevR" size={15} style={{ color: 'var(--gos-text-3)', flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountContactsTab;
